/**
 * Client Actions Card
 * 
 * Navigation actions to existing pages.
 * No new controls - only links to existing functionality.
 * 
 * PRINCIPLES:
 * - Provide helpful next steps
 * - Use existing routes only
 * - Contextual action highlighting based on status
 */

import { Link } from "react-router-dom";
import { 
  TrendingUp, 
  FileText,
  BarChart3, 
  MessageCircle,
  ChevronRight,
  Zap
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { DeliveryStatus } from "./DeliveryOverviewCard";

interface ClientActionsCardProps {
  status: DeliveryStatus;
}

interface ActionItem {
  label: string;
  description: string;
  href: string;
  icon: typeof TrendingUp;
  highlighted?: boolean;
}

function getActions(status: DeliveryStatus): ActionItem[] {
  const baseActions: ActionItem[] = [
    {
      label: "Increase Delivery Speed",
      description: "Adjust velocity to receive leads faster",
      href: "/dashboard/delivery-settings",
      icon: TrendingUp,
      highlighted: status === 'SLIGHTLY_BEHIND' || status === 'AT_RISK',
    },
    {
      label: "Contract & Billing",
      description: "Manage your subscription, billing, and delivery",
      href: "/dashboard/wallet",
      icon: FileText,
    },
    {
      label: "View Performance",
      description: "See your response times and close rates",
      href: "/dashboard/performance",
      icon: BarChart3,
    },
    {
      label: "Contact Support",
      description: "Get help from your account manager",
      href: "/dashboard/messages",
      icon: MessageCircle,
    },
  ];

  // Sort highlighted actions to the top
  return baseActions.sort((a, b) => {
    if (a.highlighted && !b.highlighted) return -1;
    if (!a.highlighted && b.highlighted) return 1;
    return 0;
  });
}

export function ClientActionsCard({ status }: ClientActionsCardProps) {
  const actions = getActions(status);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Zap className="w-5 h-5 text-accent" />
          Quick Actions
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-2">
        {actions.map((action) => {
          const Icon = action.icon;
          
          return (
            <Link key={action.href} to={action.href}>
              <div className={cn(
                "flex items-center gap-4 p-3 rounded-lg transition-all group",
                "hover:bg-muted/80",
                action.highlighted && "bg-accent/5 border border-accent/20 hover:bg-accent/10"
              )}>
                <div className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center",
                  action.highlighted 
                    ? "bg-accent/10 text-accent" 
                    : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                )}>
                  <Icon className="w-5 h-5" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-foreground">{action.label}</p>
                    {action.highlighted && (
                      <span className="text-xs text-accent font-medium">Recommended</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{action.description}</p>
                </div>
                
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              </div>
            </Link>
          );
        })}
      </CardContent>
    </Card>
  );
}
